import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const Field = ({ id, label, ...props }) => (
  <label htmlFor={id} className="block">
    <span className="mb-1.5 block text-sm font-medium text-[#3f3f46]">{label}</span>
    <input
      id={id}
      className="h-12 w-full rounded-xl border border-[#e2e2e6] bg-[#F5F5F7] px-4 text-base text-[#111111] outline-none transition-colors placeholder:text-[#8a8a93] focus:border-[#0066FF] focus:bg-white focus:ring-4 focus:ring-[#0066FF]/10"
      {...props}
    />
  </label>
);

const Success = ({ result, onClose }) => (
  <div className="py-4 text-center" data-testid="waitlist-success">
    <motion.div
      initial={{ scale: 0, rotate: -30 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 16 }}
      className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#0066FF] text-white shadow-[0_16px_40px_-12px_rgba(0,102,255,0.8)]"
    >
      <Check className="h-8 w-8" strokeWidth={3} />
    </motion.div>
    <DialogTitle className="font-display mt-6 text-2xl font-bold tracking-tight">
      {result.gia_iscritto ? "Sei già in lista!" : `Sei dentro, ${result.nome}!`}
    </DialogTitle>
    <DialogDescription className="mt-2 text-[#52525b]">
      {result.gia_iscritto
        ? "Questa email è già iscritta. Ti avviseremo appena esce il prossimo tool."
        : `Sei il numero #${result.posizione} in lista d'attesa. Ti scriveremo appena esce il prossimo tool.`}
    </DialogDescription>
    <button onClick={onClose} className="btn-ghost mt-7 h-11 px-6 text-sm" data-testid="waitlist-success-close">
      Perfetto, grazie
    </button>
  </div>
);

export const WaitlistDialog = ({ open, onOpenChange, fonte }) => {
  const [form, setForm] = useState({ nome: "", email: "", telefono: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/waitlist", { ...form, fonte });
      setResult({ ...data, nome: form.nome.split(" ")[0] });
      setForm({ nome: "", email: "", telefono: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const handleOpen = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => setResult(null), 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent
        data-lenis-prevent
        className="w-[calc(100%-2rem)] max-w-md rounded-[1.75rem] border-[#e7e7ea] p-7 sm:p-9"
        data-testid="waitlist-modal"
      >
        {result ? (
          <Success result={result} onClose={() => handleOpen(false)} />
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="waitlist-form">
            <div>
              <span className="eyebrow">Lista d'attesa · Gratis</span>
              <DialogTitle className="font-display mt-3 text-[1.7rem] font-bold leading-tight tracking-tight">
                Tieniti un posto in prima fila
              </DialogTitle>
              <DialogDescription className="mt-2 text-[0.95rem] text-[#52525b]">
                Lascia i tuoi dati: sarai tra i primi a provare MIMIS Video Italia.
              </DialogDescription>
            </div>
            <Field id="wl-nome" label="Nome" required minLength={2} value={form.nome} onChange={set("nome")} placeholder="Mario Rossi" autoComplete="name" data-testid="waitlist-name-input" />
            <Field id="wl-email" label="Email" type="email" required value={form.email} onChange={set("email")} placeholder="mario@email.it" autoComplete="email" data-testid="waitlist-email-input" />
            <Field id="wl-tel" label="Telefono" type="tel" required minLength={6} value={form.telefono} onChange={set("telefono")} placeholder="+39 333 123 4567" autoComplete="tel" data-testid="waitlist-phone-input" />
            <button type="submit" disabled={busy} className="btn-blu h-12 w-full text-[0.95rem]" data-testid="waitlist-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Entra in Lista d'Attesa <ArrowRight className="arrow h-4 w-4" /></>}
            </button>
            <p className="text-center text-xs text-[#71717a]">Niente spam. Ti scriviamo solo quando il tool è pronto.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
